import type { PrinterVendor, PrinterTransport } from './adapters/types';
import { detectVendor } from './adapters/types';
import type { BtDevice } from './bt/BluetoothBridge';

/**
 * Printer discovery — network (mDNS / Epson ePOS2Discovery / TCP:9100 scan)
 * and Bluetooth (via the platform BT bridge).
 *
 * Pure TS. Uses the native bridge when present:
 *   - window.EpsonEpos.discover(timeoutMs, cb)  → Epson LAN/BT via ePOS2Discovery
 *   - window.VisionPosBTBridge.scan(timeoutMs)  → BT SPP scan (all vendors)
 *
 * Falls back to a TCP probe when no bridge is available (dev browser).
 */

export interface DiscoveredPrinter {
  vendor: PrinterVendor;
  /** Human label (device name or "Epson TM-T88VI @ 192.168.1.42"). */
  name: string;
  transport: PrinterTransport;
  ip?: string;
  port?: number;
  mac?: string;
  /** Epson series if reported by ePOS2Discovery. */
  eposSeries?: string;
  /** ESC/POS probe reply byte (0x12 = printer OK) — network only. */
  escposOnline?: boolean;
  /** Discovery source (for debugging / UI badge). */
  source: 'epos2' | 'bt' | 'tcp9100';
}

export interface DiscoveryOptions {
  timeoutMs?: number;
  /** Restrict discovery to a specific transport. */
  transport?: PrinterTransport | 'any';
  /** Subnet to scan for TCP:9100 fallback (e.g. "192.168.1"). */
  subnet?: string;
  /** Max concurrent TCP probes. */
  concurrency?: number;
}

/** Entry point — aggregate all sources into a deduplicated list. */
export async function discoverPrinters(opts: DiscoveryOptions = {}): Promise<DiscoveredPrinter[]> {
  const transport = opts.transport ?? 'any';
  const results: DiscoveredPrinter[] = [];

  const tasks: Promise<DiscoveredPrinter[]>[] = [];

  if (transport === 'any' || transport === 'network') {
    tasks.push(discoverEpsonEpos(opts));
    // TCP /24 scan runs whenever the native bridge is available (auto-derives subnet from Wi-Fi)
    const netBridge: any = (window as any).VisionPosNetScan;
    if (opts.subnet || (netBridge && netBridge.isAvailable?.())) {
      tasks.push(scanTcp9100(opts.subnet ?? '', opts));
    }
  }
  if (transport === 'any' || transport === 'bluetooth') {
    tasks.push(discoverBluetooth(opts));
  }

  const parts = await Promise.allSettled(tasks);
  for (const p of parts) {
    if (p.status === 'fulfilled') results.push(...p.value);
  }
  return dedupe(results);
}

// ─────────────── Epson ePOS2 discovery ───────────────

async function discoverEpsonEpos(opts: DiscoveryOptions): Promise<DiscoveredPrinter[]> {
  const b: any = (window as any).EpsonEpos;
  if (!b?.startDiscovery || !b?.getDiscoveryResults) return [];

  const timeout = opts.timeoutMs ?? 5000;
  try {
    const ok = b.startDiscovery(JSON.stringify({ portType: 'ALL', timeoutMs: timeout }));
    if (!ok) return [];
  } catch {
    return [];
  }

  const deadline = Date.now() + timeout + 1500;
  let devices: any[] = [];
  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, 500));
    try {
      const res = JSON.parse(b.getDiscoveryResults() || '{}');
      devices = res.devices || [];
      if (!res.running) break;
    } catch {
      /* ignore */
    }
  }

  return (devices || []).map((d: any) => ({
    vendor: 'epson' as const,
    name: d.deviceName || `Epson @ ${d.ipAddress || d.target}`,
    transport: (d.target?.startsWith('BT:') ? 'bluetooth' : 'network') as PrinterTransport,
    ip: d.ipAddress,
    mac: d.macAddress,
    port: d.target?.startsWith('TCP:') ? 9100 : undefined,
    source: 'epos2' as const,
  }));
}

// ─────────────── Bluetooth scan ───────────────

async function discoverBluetooth(opts: DiscoveryOptions): Promise<DiscoveredPrinter[]> {
  const b: any = (window as any).VisionPosBTBridge || (window as any).BluetoothBridge;
  if (!b?.scan && !b?.listPaired) return [];

  const timeout = opts.timeoutMs ?? 8000;
  let devices: BtDevice[] = [];
  try {
    if (b.scan) {
      const json = await b.scan(timeout);
      devices = typeof json === 'string' ? JSON.parse(json) : json;
    } else if (b.listPaired) {
      const json = await b.listPaired();
      devices = typeof json === 'string' ? JSON.parse(json) : json;
    }
  } catch {
    return [];
  }

  return (devices || []).map((d) => ({
    vendor: detectVendor(d),
    name: d.name || d.address || 'Imprimante BT',
    transport: 'bluetooth' as const,
    mac: d.address,
    source: 'bt' as const,
  }));
}

// ─────────────── TCP:9100 subnet fallback ───────────────

/**
 * Fallback scan — only usable from a native bridge because browsers
 * cannot open raw TCP sockets. Requires `window.VisionPosNetScan.probe9100(ip)`.
 */
async function scanTcp9100(subnet: string, opts: DiscoveryOptions): Promise<DiscoveredPrinter[]> {
  const b: any = (window as any).VisionPosNetScan;
  if (!b?.scan || !b?.getResults) return [];

  try {
    const ok = b.scan(JSON.stringify({ port: 9100, timeoutMs: 400, subnet }));
    if (!ok) return [];
  } catch {
    return [];
  }

  const deadline = Date.now() + (opts.timeoutMs ?? 15000);
  let devices: any[] = [];
  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, 700));
    try {
      const res = JSON.parse(b.getResults() || '{}');
      devices = res.devices || [];
      if (!res.scanning) break;
    } catch {
      /* ignore */
    }
  }

  return (devices || []).map((d: any) => ({
    vendor: 'generic' as PrinterVendor,
    name: `Imprimante @ ${d.ip}`,
    transport: 'network' as const,
    ip: d.ip,
    port: d.port || 9100,
    escposOnline: true,
    source: 'tcp9100' as const,
  }));
}

// ─────────────── Helpers ───────────────

function dedupe(list: DiscoveredPrinter[]): DiscoveredPrinter[] {
  const seen = new Set<string>();
  const out: DiscoveredPrinter[] = [];
  for (const p of list) {
    const key = [p.transport, p.ip || p.mac || p.name].join('|');
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(p);
  }
  return out;
}
